import React, { useState, useEffect } from 'react';
import { Gear, User, Shield, Building, Info, Cloud, CloudSlash, ArrowsClockwise, CheckCircle, XCircle, SignOut, ArrowSquareOut, GoogleLogo } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = ({ user }) => {
  // OneDrive state
  const [onedriveStatus, setOnedriveStatus] = useState(null);
  const [onedriveLoading, setOnedriveLoading] = useState(true);
  const [onedriveSyncing, setOnedriveSyncing] = useState(false);
  const [onedriveSyncProgress, setOnedriveSyncProgress] = useState(null);

  // Google Drive state
  const [gdriveStatus, setGdriveStatus] = useState(null);
  const [gdriveLoading, setGdriveLoading] = useState(true);
  const [gdriveSyncing, setGdriveSyncing] = useState(false);
  const [gdriveSyncProgress, setGdriveSyncProgress] = useState(null);

  // Storage usage state
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);

  const isSuperAdmin = user?.is_super_admin || user?.role?.toLowerCase() === 'admin';

  const formatBytes = (bytes) => {
    const b = Number(bytes) || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const fetchStorageUsage = async () => {
    setStorageLoading(true);
    try {
      const res = await axios.get(`${API}/admin/storage-usage`);
      setStorage(res.data);
    } catch (error) {
      console.error('Failed to fetch storage usage:', error);
    } finally {
      setStorageLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchStorageUsage();
  }, [isSuperAdmin]);

  useEffect(() => {
    checkOnedriveStatus();
    checkGdriveStatus();
    
    // Check for OAuth callback params
    const params = new URLSearchParams(window.location.search);
    
    // OneDrive callbacks
    if (params.get('onedrive_connected') === 'true') {
      toast.success('OneDrive connected successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (params.get('onedrive_error')) {
      toast.error(`OneDrive connection failed: ${params.get('onedrive_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Google Drive callbacks
    if (params.get('gdrive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      checkGdriveStatus();
    }
    if (params.get('gdrive_error')) {
      toast.error(`Google Drive connection failed: ${params.get('gdrive_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // ============ OneDrive Functions ============
  const checkOnedriveStatus = async () => {
    try {
      setOnedriveLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/onedrive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnedriveStatus(response.data);
    } catch (error) {
      console.error('Failed to check OneDrive status:', error);
      setOnedriveStatus({ connected: false, message: 'Failed to check status' });
    } finally {
      setOnedriveLoading(false);
    }
  };

  const connectOnedrive = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/onedrive/auth/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate OneDrive connection');
    }
  };

  const disconnectOnedrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect OneDrive?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onedrive/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OneDrive disconnected');
      setOnedriveStatus({ connected: false });
    } catch (error) {
      toast.error('Failed to disconnect OneDrive');
    }
  };

  const syncAllToOnedrive = async () => {
    try {
      setOnedriveSyncing(true);
      setOnedriveSyncProgress({ status: 'Starting sync...' });
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/onedrive/sync/all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOnedriveSyncProgress({ status: 'Complete', ...response.data });
      toast.success(`Synced ${response.data.synced} documents to OneDrive`);
    } catch (error) {
      toast.error('Sync failed: ' + (error.response?.data?.detail || error.message));
      setOnedriveSyncProgress(null);
    } finally {
      setOnedriveSyncing(false);
    }
  };

  // ============ Google Drive Functions ============
  const checkGdriveStatus = async () => {
    try {
      setGdriveLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gdrive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGdriveStatus(response.data);
      
      // Also check migration status if connected
      if (response.data.connected) {
        try {
          const migrationResponse = await axios.get(`${API}/gdrive/migration-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setGdriveStatus(prev => ({ ...prev, migration: migrationResponse.data }));
        } catch (e) {
          console.log('Migration status check skipped');
        }
      }
    } catch (error) {
      console.error('Failed to check Google Drive status:', error);
      setGdriveStatus({ connected: false, message: 'Failed to check status' });
    } finally {
      setGdriveLoading(false);
    }
  };

  const connectGdrive = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gdrive/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google Drive connection');
    }
  };

  const disconnectGdrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/gdrive/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google Drive disconnected');
      setGdriveStatus({ connected: false });
    } catch (error) {
      toast.error('Failed to disconnect Google Drive');
    }
  };

  const syncAllToGdrive = async () => {
    try {
      setGdriveSyncing(true);
      setGdriveSyncProgress({ status: 'Starting sync...' });
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/gdrive/sync/all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGdriveSyncProgress({ status: 'Complete', ...response.data });
      toast.success(`Synced ${response.data.synced} documents to Google Drive`);
    } catch (error) {
      toast.error('Sync failed: ' + (error.response?.data?.detail || error.message));
      setGdriveSyncProgress(null);
    } finally {
      setGdriveSyncing(false);
    }
  };

  // Migrate files from preview environment to Google Drive
  const migrateFromPreview = async () => {
    try {
      setGdriveSyncing(true);
      setGdriveSyncProgress({ status: 'Connecting to preview server and fetching file manifest...' });
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/gdrive/migrate-from-preview`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 600000 // 10 minute timeout for migration
      });
      
      const results = response.data.results || {};
      setGdriveSyncProgress({ 
        status: 'Complete', 
        total: (results.synced?.length || 0) + (results.failed?.length || 0) + (results.already_synced?.length || 0) + (results.no_match?.length || 0),
        synced: results.synced?.length || 0,
        failed: results.failed?.length || 0,
        skipped: results.already_synced?.length || 0,
        noMatch: results.no_match?.length || 0,
        details: results
      });
      
      if (results.synced?.length > 0) {
        toast.success(`Successfully migrated ${results.synced.length} files to Google Drive!`);
      } else if (results.no_match?.length > 0) {
        toast.warning(`${results.no_match.length} files need re-upload - originals not found`);
      } else {
        toast.info(response.data.message || 'Migration completed - no new files to migrate');
      }
    } catch (error) {
      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
      setGdriveSyncProgress(null);
    } finally {
      setGdriveSyncing(false);
    }
  };
  
  // Check for orphaned files
  const checkOrphanedFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gdrive/orphaned-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to check orphaned files:', error);
      return null;
    }
  };

  // ============ Sync Progress Component ============
  const SyncProgressCard = ({ progress }) => {
    if (!progress) return null;
    
    return (
      <div className="p-4 bg-gray-50 rounded-xl mt-4">
        <h4 className="font-medium mb-2 text-sm">Sync Progress</h4>
        {progress.status === 'Complete' ? (
          <div className="space-y-1 text-sm">
            <p className="text-green-600 flex items-center gap-1">
              <CheckCircle size={14} weight="fill" /> Operation completed
            </p>
            <p>Total processed: {progress.total}</p>
            <p className="text-green-600">Successfully synced: {progress.synced}</p>
            {progress.failed > 0 && (
              <p className="text-red-600">Failed: {progress.failed}</p>
            )}
            {progress.skipped > 0 && (
              <p className="text-gray-500">Already synced: {progress.skipped}</p>
            )}
            {progress.noMatch > 0 && (
              <p className="text-orange-500">No match on preview: {progress.noMatch}</p>
            )}
            
            {/* Show details for failed items */}
            {progress.details?.failed?.length > 0 && (
              <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                <p className="font-medium text-red-700 mb-1">Failed items:</p>
                {progress.details.failed.slice(0, 3).map((item, idx) => (
                  <p key={idx} className="text-red-600 truncate">
                    {item.document_number || item.cycle_id}: {item.reason}
                  </p>
                ))}
                {progress.details.failed.length > 3 && (
                  <p className="text-red-500">...and {progress.details.failed.length - 3} more</p>
                )}
              </div>
            )}
            
            {/* Show details for no-match items */}
            {progress.details?.no_match?.length > 0 && (
              <div className="mt-2 p-2 bg-orange-50 rounded text-xs">
                <p className="font-medium text-orange-700 mb-1">
                  ⚠️ Files need re-upload (originals lost):
                </p>
                <p className="text-orange-600 mb-2 text-[11px]">
                  These files were uploaded but never synced to Google Drive before the server restarted.
                  They must be re-uploaded manually.
                </p>
                {progress.details.no_match.slice(0, 3).map((item, idx) => (
                  <p key={idx} className="text-orange-600 truncate">
                    • {item.document_number} Rev {item.revision}
                  </p>
                ))}
                {progress.details.no_match.length > 3 && (
                  <p className="text-orange-500">...and {progress.details.no_match.length - 3} more</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600 text-sm">{progress.status}</p>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 mb-1">Settings</h1>
        <p className="text-gray-500">Manage your account and system preferences</p>
      </div>
      
      {/* Google Drive Integration Card - PRIMARY */}
      <div className="mb-6 bg-white rounded-2xl p-6 shadow-apple-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-yellow-50 rounded-xl flex items-center justify-center">
            <GoogleLogo size={20} weight="bold" className="text-yellow-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Google Drive Integration
            </h3>
            <p className="text-xs text-gray-500">Backup documents to your personal Google Drive</p>
          </div>
          <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Recommended</span>
        </div>
        
        {gdriveLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking connection status...
          </div>
        ) : gdriveStatus?.connected ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-xl border border-green-100">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <CheckCircle size={18} weight="fill" />
                <span className="font-medium">Connected to Google Drive</span>
              </div>
              <div className="text-sm text-green-600 space-y-1">
                <p><strong>Account:</strong> {gdriveStatus.user_name}</p>
                <p><strong>Email:</strong> {gdriveStatus.user_email}</p>
                <p><strong>Sync Folder:</strong> Google Drive/{gdriveStatus.root_folder}</p>
              </div>
            </div>
            
            {/* Migration Status */}
            {gdriveStatus.migration && (
              <div className={`p-4 rounded-xl border ${gdriveStatus.migration.cycles_needing_migration > 0 ? 'bg-orange-50 border-orange-100' : 'bg-blue-50 border-blue-100'}`}>
                <p className="text-sm font-medium mb-2">File Sync Status:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p>Total cycles: <strong>{gdriveStatus.migration.total_cycles}</strong></p>
                  <p>With local PDF: <strong>{gdriveStatus.migration.cycles_with_local_pdf}</strong></p>
                  <p className="text-green-600">Synced to GDrive: <strong>{gdriveStatus.migration.cycles_with_gdrive}</strong></p>
                  {gdriveStatus.migration.cycles_needing_migration > 0 && (
                    <p className="text-orange-600">Needs migration: <strong>{gdriveStatus.migration.cycles_needing_migration}</strong></p>
                  )}
                </div>
                {gdriveStatus.migration.cycles_needing_migration > 0 && (
                  <p className="text-xs text-orange-600 mt-2">
                    ⚠️ Some files need to be migrated from preview. Click "Migrate from Preview" below.
                  </p>
                )}
              </div>
            )}
            
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={syncAllToGdrive} 
                disabled={gdriveSyncing}
                className="bg-yellow-600 hover:bg-yellow-700"
                data-testid="gdrive-sync-btn"
              >
                {gdriveSyncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <ArrowsClockwise size={16} weight="bold" className="mr-2" />
                    Sync All Documents
                  </>
                )}
              </Button>
              
              <Button 
                onClick={migrateFromPreview} 
                disabled={gdriveSyncing}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="gdrive-migrate-btn"
              >
                {gdriveSyncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Migrating...
                  </>
                ) : (
                  <>
                    <ArrowsClockwise size={16} weight="bold" className="mr-2" />
                    Migrate from Preview
                  </>
                )}
              </Button>
              
              <Button variant="outline" onClick={checkGdriveStatus}>
                <ArrowsClockwise size={16} className="mr-2" />
                Refresh Status
              </Button>
              
              <Button 
                variant="outline" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={disconnectGdrive}
              >
                <SignOut size={16} className="mr-2" />
                Disconnect
              </Button>
            </div>
            
            <SyncProgressCard progress={gdriveSyncProgress} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <CloudSlash size={18} />
                <span className="font-medium">Google Drive Not Connected</span>
              </div>
              <p className="text-sm text-gray-500">
                Connect your Google account to automatically backup documents to your personal cloud storage.
                Files will sync to: <strong>Google Drive/DocStat/Documents</strong>
              </p>
            </div>
            
            <Button onClick={connectGdrive} className="bg-yellow-600 hover:bg-yellow-700" data-testid="gdrive-connect-btn">
              <GoogleLogo size={16} weight="bold" className="mr-2" />
              Connect Google Drive
            </Button>
            
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
              <p className="text-xs text-yellow-700">
                💡 <strong>Recommended:</strong> Google Drive works with any personal Gmail account.
                15GB free storage included with every Google account.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* OneDrive Integration Card - SECONDARY */}
      <div className="mb-6 bg-white rounded-2xl p-6 shadow-apple-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <Cloud size={20} weight="regular" className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              OneDrive Integration
            </h3>
            <p className="text-xs text-gray-500">Backup documents to Microsoft OneDrive (requires work account)</p>
          </div>
        </div>
        
        {onedriveLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking connection status...
          </div>
        ) : onedriveStatus?.connected ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-xl border border-green-100">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <CheckCircle size={18} weight="fill" />
                <span className="font-medium">Connected to OneDrive</span>
              </div>
              <div className="text-sm text-green-600 space-y-1">
                <p><strong>Account:</strong> {onedriveStatus.user_name}</p>
                <p><strong>Email:</strong> {onedriveStatus.user_email}</p>
                <p><strong>Sync Folder:</strong> OneDrive/{onedriveStatus.root_folder}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={syncAllToOnedrive} 
                disabled={onedriveSyncing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {onedriveSyncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <ArrowsClockwise size={16} weight="bold" className="mr-2" />
                    Sync All Documents
                  </>
                )}
              </Button>
              
              <Button variant="outline" onClick={checkOnedriveStatus}>
                <ArrowsClockwise size={16} className="mr-2" />
                Refresh Status
              </Button>
              
              <Button 
                variant="outline" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={disconnectOnedrive}
              >
                <SignOut size={16} className="mr-2" />
                Disconnect
              </Button>
            </div>
            
            <SyncProgressCard progress={onedriveSyncProgress} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <CloudSlash size={18} />
                <span className="font-medium">OneDrive Not Connected</span>
              </div>
              <p className="text-sm text-gray-500">
                Connect your Microsoft work/school account to sync documents to OneDrive.
                <br />
                <span className="text-orange-600">⚠️ Requires Microsoft 365 work/school account with SharePoint license.</span>
              </p>
            </div>
            
            <Button onClick={connectOnedrive} className="bg-blue-600 hover:bg-blue-700">
              <Cloud size={16} weight="fill" className="mr-2" />
              Connect OneDrive
            </Button>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-6 shadow-apple-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <User size={20} weight="regular" className="text-blue-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">
              Profile Information
            </h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Full Name</p>
              <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Email</p>
              <p className="text-sm font-medium text-gray-900">{user.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Role</p>
              <span className="inline-flex px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg capitalize">
                {user.role}
              </span>
            </div>
            {user.department && (
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Department</p>
                <p className="text-sm font-medium text-gray-900">{user.department}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* System Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-apple-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
              <Gear size={20} weight="regular" className="text-gray-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">
              System Information
            </h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">System Version</p>
              <p className="text-sm font-medium text-gray-900">DocStat EDMS v1.0.0</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Environment</p>
              <span className="inline-flex px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg">
                Production
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Storage Usage Section */}
      {isSuperAdmin && (
        <div className="mt-6 bg-white rounded-2xl p-6 shadow-apple-sm border border-gray-100" data-testid="storage-usage-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Cloud size={20} weight="regular" className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Storage Usage</h3>
                <p className="text-xs text-gray-500">Total size of all files uploaded to this system</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchStorageUsage}
              disabled={storageLoading}
              className="rounded-xl"
              data-testid="refresh-storage-usage"
            >
              {storageLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {!storage ? (
            <p className="text-sm text-gray-500">{storageLoading ? 'Calculating...' : 'Storage usage unavailable.'}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-8 mb-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Total Used</p>
                  <p className="text-3xl font-semibold text-gray-900" data-testid="storage-total">
                    {formatBytes(storage.total_bytes)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Files</p>
                  <p className="text-xl font-medium text-gray-900">{storage.total_files}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Documents</p>
                  <p className="text-xl font-medium text-gray-900">{storage.document_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Revisions</p>
                  <p className="text-xl font-medium text-gray-900">{storage.revision_count}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {Object.entries(storage.breakdown || {}).map(([key, val]) => (
                  <div key={key} className="p-3 bg-gray-50 rounded-xl" data-testid={`storage-breakdown-${key}`}>
                    <p className="text-xs text-gray-500 mb-1">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </p>
                    <p className="text-sm font-semibold text-gray-900">{formatBytes(val.bytes)}</p>
                    <p className="text-xs text-gray-400">{val.files} file(s)</p>
                  </div>
                ))}
              </div>

              {(storage.by_project || []).length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">By Project (original documents)</p>
                  <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                    {storage.by_project.map((row) => (
                      <div key={row.project_id || 'none'} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="text-gray-700 truncate">{row.project_name}</span>
                        <span className="text-gray-900 font-medium">{formatBytes(row.bytes)} · {row.files} file(s)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* About Section */}
      <div className="mt-6 bg-white rounded-2xl p-6 shadow-apple-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
            <Info size={20} weight="regular" className="text-purple-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">
            About DocStat EDMS
          </h3>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          DocStat is an Enterprise Document Management System designed to help organizations manage,
          store, and collaborate on documents efficiently. It features advanced review workflows,
          transmittal management, and role-based access control to streamline your document processes.
        </p>
      </div>
    </div>
  );
};

export default Settings;
